import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { EventJourneyStatus, Role } from "@prisma/client";
import { toast } from "react-toastify";
import Link from "next/link";
import { useSession } from "next-auth/react";

import { api } from "~/utils/api";
import { isValidPlaceType } from "~/utils/event";
import Modal from "~/components/v3/themed/Modal";
import { Button } from "~/components/v4/themed/Button";
import { SystemMessage } from "~/components/v4/themed/SystemMessage";
import { toastHandler } from "~/components/ToastHandler";
import { ConfirmActionModal } from "~/components/v4/shadcn/ConfirmActionModal";
import {
    LocationSelector,
    Location,
} from "~/components/v3/shadcn/LocationSelector";

import { confirmEventVenue } from "../manage/more/actions";
import { GetEventFromSlug } from "../actions";
import { changeStatus } from "../actions";
import { useWhiteLabel } from "~/app/providers";
import { cn } from "~/utils/fontsConfig";

const locationIsValid = (
    hostHasConfirmedIsValidLocation: boolean | null,
    googleMapsPlaceName?: string | null,
    placeType?: string[],
) => {
    // if the place name contains numbers, its probably a real address / venue
    // otherwise, just check if host has confirmed the venue
    if (
        (googleMapsPlaceName && /\d/.test(googleMapsPlaceName)) ||
        hostHasConfirmedIsValidLocation === true ||
        (isValidPlaceType(placeType || []) &&
            (googleMapsPlaceName || "").length > 0)
    ) {
        return true;
    }

    return false;
};

const EventStatusWarnings = ({ event }: { event: GetEventFromSlug }) => {
    const router = useRouter();
    const [confirmSendForApprovalModal, setConfirmSendForApprovalModal] =
        useState(false);
    const [isVisibleConfirmStatusChange, setIsVisibleConfirmStatusChange] =
        useState(false);

    const submitForApprovalMutation = api.event.submitForApproval.useMutation();

    const utils = api.useUtils();

    const handleDeleteEvent = async () => {
        await toastHandler({
            loadingMessage: "Removing event...",
            successMessage: "Event removed successfully",
            errorMessage: "Failed to remove event",
            operation: async () => {
                await changeStatus({
                    eventSlug: event.slug,
                    status: EventJourneyStatus.DELETED,
                });
            },
            onSuccess: () => {
                router.back();
            },
            onSettled: () => {
                setIsVisibleConfirmStatusChange(false);
            },
        });
    };

    const handleSendForApproval = async () => {
        toast.loading("Sending event for approval", {
            toastId: "send-for-approval-loading",
        });

        await submitForApprovalMutation.mutateAsync(
            {
                eventId: event.id,
                communityId: event?.community?.id,
            },
            {
                onSuccess: async () => {
                    toast.success("Event sent for approval", {
                        autoClose: 1200,
                    });
                    utils.event.getEventJounreyStatus.invalidate();
                    router.refresh();
                },
                onError: (e) => {
                    toast.dismiss("send-for-approval-loading");
                    console.error(e);
                },
                onSettled: () => {
                    toast.dismiss("send-for-approval-loading");
                },
            },
        );
    };

    if (event.eventJourneyStatus !== EventJourneyStatus.DRAFT_PROPOSED)
        return false;

    return (
        <>
            <SystemMessage
                className="bg-v4-SUNSHINE"
                title="This event is currently a draft."
                message={`Submit your event proposal for review once you're done filling out the event details.`}
                footer={
                    <div className="flex flex-wrap gap-2">
                        <Button
                            onClick={() => {
                                setConfirmSendForApprovalModal(true);
                            }}
                            variant="secondary"
                            className="h-[30px] py-1 text-[14px] shadow-none"
                        >
                            Submit for Approval
                        </Button>
                        <Button
                            onClick={() => {
                                setIsVisibleConfirmStatusChange(true);
                            }}
                            variant="secondary"
                            className="h-[30px] py-1 text-[14px] shadow-none"
                        >
                            Delete Draft
                        </Button>
                    </div>
                }
            />

            <ConfirmActionModal
                isOpen={isVisibleConfirmStatusChange}
                onClose={() => setIsVisibleConfirmStatusChange(false)}
                onConfirm={handleDeleteEvent}
                title="Delete Draft"
                description="Are you sure you want to delete this draft? This action cannot be undone."
                confirmText="Delete Draft"
                variant="destructive"
            />

            <ConfirmSendForApprovalModal
                open={confirmSendForApprovalModal}
                setIsOpen={setConfirmSendForApprovalModal}
                onConfirm={handleSendForApproval}
                event={event}
            />
        </>
    );
};

const ConfirmSendForApprovalModal = ({
    open,
    setIsOpen,
    onConfirm,
    event,
}: {
    onConfirm: () => void;
    open: boolean;
    setIsOpen: (open: boolean) => void;
    event: GetEventFromSlug;
}) => {
    const [isLocationValid, setIsLocationValid] = useState(false);
    const customization = useWhiteLabel()?.customization;
    const { data: session } = useSession();

    const [location, setLocation] = useState<Location>({
        type: "physical",
        address: event.location,
        name: event.locationName ?? "",
        city: event.city ?? "",
        country: event.country ?? "",
        latitude: event.latitude ?? undefined,
        longitude: event.longitude ?? undefined,
    });

    useEffect(() => {
        setIsLocationValid(
            locationIsValid(
                event.hostHasConfirmedIsValidLocation,
                event.googleMapsPlaceName,
                event.googleMapsPlaceTypes,
            ),
        );
    }, [event]);

    const handleConfirmVenue = async () => {
        await toastHandler({
            loadingMessage: "Confirming venue...",
            successMessage: "Venue confirmed",
            errorMessage: "Failed to confirm venue",
            operation: () =>
                confirmEventVenue({
                    eventId: event.id,
                    eventSlug: event.slug,
                    location,
                }),
        });
    };

    const isLoggedIn = Boolean(session && session.user.role !== Role.ANONYMOUS);

    // isTimeSet will always be true on new event pages
    const proceedToApproval =
        event.isVirtual || (event.isTimeSet && isLocationValid);

    return (
        <Modal
            title="Send for approval"
            open={open}
            onOpenChange={setIsOpen}
            className="overflow-y-visible"
            customization={customization}
        >
            <div className={customization?.descriptionClassName}>
                {!isLoggedIn && (
                    <div className="flex flex-col gap-4">
                        <span className="text-sm">
                            Kindly login to send your event for approval.
                        </span>

                        <Link
                            href={`/login?callbackUrl=${encodeURIComponent(
                                `/beta/events/${event.slug}`,
                            )}`}
                            className="w-full pb-4"
                        >
                            <Button className="w-full">Login</Button>
                        </Link>
                    </div>
                )}

                {isLoggedIn && !isLocationValid && (
                    <div className="flex flex-col gap-4">
                        <span
                            className={cn(
                                "text-sm",
                                customization?.labelClassName || "text-white",
                            )}
                        >
                            Invalid venue
                        </span>
                        <p
                            className={cn(
                                "text-sm",
                                customization?.descriptionClassName,
                            )}
                        >
                            The specified address does not seem to point to a
                            venue. Press the button below to confirm that this
                            is the correct location.
                        </p>

                        <div className="flex flex-col gap-3">
                            <LocationSelector
                                value={location}
                                onLocationSelect={(location) => {
                                    setLocation(location);
                                }}
                                customization={customization?.selectInput}
                            />

                            <div className="flex w-full justify-end pb-4">
                                <Button
                                    onClick={handleConfirmVenue}
                                    className={customization?.buttons?.default}
                                >
                                    {event.location !== location.address
                                        ? "Update and confirm location"
                                        : "Confirm location"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {isLoggedIn && proceedToApproval && (
                    <div className="flex flex-col gap-4">
                        <p className="text-sm">
                            Are you sure you want to send this event for
                            approval? It will be reviewed by a community manager
                            shortly and you'll be notified once it's approved.
                        </p>

                        <div className="flex w-full justify-between gap-2 pb-4">
                            <Button
                                variant="destructive"
                                onClick={() => setIsOpen(false)}
                                className={customization?.buttons?.secondary}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={onConfirm}
                                disabled={!isLocationValid}
                                className={customization?.buttons?.default}
                            >
                                Submit for Approval
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default EventStatusWarnings;
